import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Search, MapPin, X, CalendarDays, Heart } from "lucide-react";

export default function VenuesDirectory() {
    const [params, setParams] = useSearchParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { fan, isFollowingVenue, followVenue, unfollowVenue } = useFan();
    const [venues, setVenues] = useState([]);
    const [meta, setMeta] = useState({ cities: [], countries: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");
    const [busyId, setBusyId] = useState(null);

    const toggleFollow = async (v, e) => {
        e.preventDefault(); e.stopPropagation();
        if (!user || !fan) {
            nav(`/signin?next=${encodeURIComponent(`/venues/${v.id}?follow=1`)}`);
            return;
        }
        setBusyId(v.id);
        try {
            if (isFollowingVenue(v.id) || v.is_following) {
                await unfollowVenue(v.id);
                setVenues((list) => list.map((x) => x.id === v.id ? { ...x, is_following: false, followers_count: Math.max(0, (x.followers_count || 1) - 1) } : x));
                toast(`Unfollowed ${v.name}`);
            } else {
                await followVenue(v.id, "discover");
                setVenues((list) => list.map((x) => x.id === v.id ? { ...x, is_following: true, followers_count: (x.followers_count || 0) + 1 } : x));
                toast.success(`Following ${v.name}`);
            }
        } catch {
            toast.error("Could not update follow.");
        } finally { setBusyId(null); }
    };

    const filters = useMemo(() => ({
        q: params.get("q") || undefined,
        city: params.get("city") || undefined,
        country: params.get("country") || undefined,
    }), [params]);

    useEffect(() => {
        setLoading(true);
        endpoints.listVenues(filters).then((d) => {
            setVenues(d.venues || []);
            setMeta({ cities: d.cities || [], countries: d.countries || [] });
        }).finally(() => setLoading(false));
    }, [filters]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };
    const clearAll = () => setParams({}, { replace: true });

    return (
        <div className="pm-fade" data-testid="venues-directory-page">
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Directory</div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">Venues</h1>
                    <p className="mt-4 text-[#a3a3a3] max-w-xl">Rooms, halls, and clubs across the Project Mint network. Follow the venues your favourite artists play.</p>
                </div>
            </section>

            <section className="border-b border-[#2a2a2a] sticky top-16 z-30 bg-[#0a0a0a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1 min-w-0">
                        <Search className="w-4 h-4 text-[#666]" />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && setFilter("q", q)}
                            onBlur={() => setFilter("q", q)}
                            placeholder="Search by name, city, country…"
                            className="bg-transparent outline-none flex-1 text-sm placeholder:text-[#666]"
                            data-testid="venues-search" />
                    </div>
                    <Select label="City" value={filters.city || ""} options={meta.cities} onChange={(v) => setFilter("city", v)} testId="venue-filter-city" />
                    <Select label="Country" value={filters.country || ""} options={meta.countries} onChange={(v) => setFilter("country", v)} testId="venue-filter-country" />
                    <button onClick={clearAll} className="pm-btn" data-testid="venues-clear-filters"><X className="w-3 h-3 mr-1" /> Clear</button>
                </div>
            </section>

            <section>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#666] mb-4" data-testid="venues-count">
                        {loading ? "Loading…" : `${venues.length} venue${venues.length === 1 ? "" : "s"}`}
                    </div>
                    {!loading && venues.length === 0 && (
                        <div className="border border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid="venues-empty">No venues match those filters.</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {venues.map((v) => {
                            const following = isFollowingVenue(v.id) || v.is_following;
                            return (
                                <Link key={v.id} to={`/venues/${v.id}`} data-testid={`directory-venue-${v.id}`}
                                    className="group bg-[#0a0a0a] p-4 flex flex-col hover:bg-[#141414] transition-colors">
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        {v.image_url || v.cover_url ? (
                                            <img src={v.cover_url || v.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                        ) : <div className="absolute inset-0 bg-[#141414]" />}
                                    </div>
                                    <div className="mt-3 flex items-center gap-1 text-[10px] font-mono uppercase text-[#ff4400]"><MapPin className="w-3 h-3" /> {v.city}, {v.country}</div>
                                    <div className="font-display font-black uppercase text-lg leading-tight mt-1">{v.name}</div>
                                    {v.description && <div className="mt-1 text-xs text-[#a3a3a3] line-clamp-2">{v.description}</div>}
                                    <div className="mt-3 flex items-center justify-between gap-2">
                                        <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-2">
                                            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {v.upcoming_events || 0} upcoming</span>
                                            <span className="flex items-center gap-1 text-[#a3a3a3]" data-testid={`directory-venue-followers-${v.id}`}><Heart className="w-3 h-3 text-[#ff4400]" /> {v.followers_count || 0}</span>
                                        </div>
                                        <button
                                            onClick={(e) => toggleFollow(v, e)}
                                            disabled={busyId === v.id}
                                            data-testid={`directory-venue-follow-${v.id}`}
                                            className={`pm-btn text-[10px] py-1 px-2 ${following ? "" : "pm-btn-primary"}`}
                                        >
                                            <Heart className="w-3 h-3 mr-1" fill={following ? "currentColor" : "none"} />
                                            {following ? "Following" : "Follow"}
                                        </button>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Select({ label, value, options, onChange, testId }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="appearance-none bg-transparent border border-[#2a2a2a] px-3 pr-8 py-2 text-sm font-mono uppercase tracking-wide text-[#f4f4f0] hover:border-[#f4f4f0] focus:border-[#ff4400] outline-none">
            <option value="" className="bg-[#141414]">{label}: All</option>
            {options.map((o) => <option key={o} value={o} className="bg-[#141414]">{o}</option>)}
        </select>
    );
}
